import {
  Badge,
  Box,
  ButtonGroup,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  Heading,
  IconButton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  IFormData,
  IOrderPackageItem,
  OrderStatusType,
  PaymentMethodType,
} from "./types";
import { path } from "../../../utils/Api";
import Card from "../../../components/card/Card";
import { paymentMethodLabelMap, statusMap } from "./data";
import { useDispatch, useSelector } from "react-redux";
import { selectHoldItems } from "./redux/selector";
import { actions } from "./redux/slice";
import { DataImportantStatusEnum } from "../user/types";
import { useRef } from "react";
import { MdPrint } from "react-icons/md";
import { BsFiletypeCsv } from "react-icons/bs";
import { useReactToPrint } from "react-to-print";

interface Props {
  form: IFormData;
}
const FieldRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) => (
  <Flex>
    <Text fontWeight="semibold" minW="180px">
      {label}:
    </Text>
    <Text>{value || "-"}</Text>
  </Flex>
);
const OrderDetailForm: React.FC<Props> = ({ form }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const holdItems = useSelector(selectHoldItems);
  const componentRef = useRef<HTMLDivElement>(null);
  const renderChecked = (itemId: string, row: IOrderPackageItem) => {
    if (row.availability && row.availability == 2) {
      return true;
    }
    return holdItems.some((item) => item === itemId);
  };

  const handleCheckboxChange = (itemId: string) => {
    dispatch(
      actions.setPackageItemHold({
        id: itemId,
      })
    );
  };
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "User Details",
  });

  const handleCsvExport = () => {
    if (!form) return;

    const csvHeaders = [
      "Order ID",
      "Status",
      "Payment Status",
      "Subtotal",
      "Shipment Capacity",
      "Shipment Charges",
      "Total Price",
      "Tax Amount",
      "Full Name",
      "Japanese Name",
      "Post Code",
      "State",
      "District",
      "Street Address",
      "Phone Number",
      "Email",
      "Company Name",
      "Japanese Company Name",
      "Department",
      "Alt Full Name",
      "Alt Japanese Name",
      "Alt Post Code",
      "Alt State",
      "Alt District",
      "Alt Street Address",
      "Alt Phone Number",
      "Alt Company Name",
      "Alt Japanese Company Name",
      "Alt Department",
      "Remarks",
      "Delivery Time Slot",
      "Payment Method",
    ];

    const csvRow = [
      form.orderId,
      form.status,
      form.payment_status,
      form.subTotal,
      form.totalShipmentCapacity,
      form.shipmentCharges,
      form.totalPrice,
      form.taxAmount,
      `${form.lastName} ${form.firstName}`,
      `${form.japaneseLastName} ${form.japaneseFirstName}`,
      form.postCode,
      form.state,
      form.district,
      form.streetAddress,
      form.mobileNumber,
      form.email,
      form.companyName,
      form.japaneseCompanyName,
      form.department,
      `${form.alternateLastName} ${form.alternateFirstName}`,
      `${form.alternateJapaneseLastName} ${form.alternateJapaneseFirstName}`,
      form.alternatePostCode,
      form.alternateState,
      form.alternateDistrict,
      form.alternateStreetAddress,
      form.alternateMobileNumber,
      form.alternateCompanyName,
      form.alternateJapaneseCompanyName,
      form.alternateDepartment,
      form.remarks,
      form.deliveryTimeSlot,
      form.paymentMethod,
    ];

    // Start building CSV
    let csvContent = `${csvHeaders.join(",")}\n${csvRow.join(",")}`;

    // Append order_items if available
    if (form.order_items?.length > 0) {
      csvContent += `\n\nOrder Items:\n`;
      csvContent += `Item Name,Quantity,Price\n`;
      form.order_items.forEach((item) => {
        csvContent += `${item?.package?.title},${item.quantity},${item.price}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Order-${form.orderId || "data"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <FormControl>
      <Flex flexDirection="column">
        <ButtonGroup justifyContent={"flex-end"} mt={8}>
          <Tooltip label={t("common.button.print")}>
            <IconButton
              aria-label="Print"
              icon={<MdPrint size={20} />}
              onClick={handlePrint}
              colorScheme="brand"
              variant="solid"
              size="md"
            />
          </Tooltip>
          <Tooltip label={t("common.button.exportCsv")}>
            <IconButton
              aria-label="Export CSV"
              icon={<BsFiletypeCsv size={20} />}
              onClick={handleCsvExport}
              colorScheme="brand"
              variant="solid"
              size="md"
            />
          </Tooltip>
        </ButtonGroup>
      </Flex>
      <Stack spacing={4} ref={componentRef} px={8}>
        <Heading size="md">
          {t("order.form.orderInfo")}{" "}
          <Badge
            colorScheme={
              form.importantStatus === DataImportantStatusEnum.IMPORTANT
                ? "green"
                : ""
            }
            color={
              form.importantStatus === DataImportantStatusEnum.IMPORTANT
                ? "green.500"
                : ""
            }
            fontSize="md"
            fontWeight="500"
            sx={{ textTransform: "capitalCase" }}
          >
            &nbsp;
          </Badge>
        </Heading>
        <FieldRow label={t("order.form.orderId")} value={form.orderId} />
        <FieldRow
          label={t("order.form.status")}
          value={t(statusMap[form.status])}
        />
        <FieldRow
          label={t("order.form.paymentStatus")}
          value={form.payment_status}
        />
        <FieldRow
          label={t("order.form.paymentMethod")}
          value={t(paymentMethodLabelMap[form.paymentMethod])}
        />
        {form.paymentMethod === PaymentMethodType.STORE_PAYMENT && (
          <FieldRow label={t("order.form.store")} value={form.store} />
        )}
        <FieldRow
          label={t("order.form.subTotal")}
          value={`¥${ Math.round(form.subTotal)}`}
        />
        <FieldRow
          label={t("order.form.shipmentCapacity")}
          value={form.totalShipmentCapacity}
        />
        <FieldRow
          label={t("order.form.shipmentCharges")}
          value={`¥${ Math.round(form.shipmentCharges)}`}
        />
        <FieldRow
          label={t("order.form.taxAmount")}
          value={`¥${ Math.round(form.taxAmount)}`}
        />
        <FieldRow
          label={t("order.form.totalPrice")}
          value={`¥${ Math.round(form.totalPrice)}`}
        />
        <Divider />

        <Heading size="sm">{t("order.form.customerInfo")}</Heading>
        <FieldRow
          label={t("order.form.name")}
          value={`${form.lastName} ${form.firstName}`}
        />
        <FieldRow
          label={t("order.form.japaneseName")}
          value={`${form.japaneseLastName} ${form.japaneseFirstName}`}
        />
        {form.customerId && form.customerId !== "" && (
          <FieldRow label={t("common.form.customerId")} value={form.customerId} />
        )}
        <FieldRow label={t("order.form.email")} value={form.email} />
        <FieldRow
          label={t("order.form.mobileNumber")}
          value={form.mobileNumber}
        />
        <FieldRow label={t("order.form.postCode")} value={form.postCode} />

        <FieldRow label={t("order.form.state")} value={form.state} />
        <FieldRow label={t("order.form.district")} value={form.district} />
        <FieldRow label={t("order.form.street")} value={form.streetAddress} />
        <FieldRow label={t("order.form.company")} value={form.companyName} />
        <FieldRow
          label={t("order.form.japaneseCompanyName")}
          value={form.japaneseCompanyName}
        />
        <FieldRow label={t("order.form.department")} value={form.department} />
        <Divider />

        <Heading size="sm">{t("order.form.alternateInfo")}</Heading>
        <FieldRow
          label={t("order.form.name")}
          value={`${form.alternateLastName} ${form.alternateFirstName}`}
        />
        <FieldRow
          label={t("order.form.japaneseName")}
          value={`${form.alternateJapaneseLastName} ${form.alternateJapaneseFirstName}`}
        />
        <FieldRow
          label={t("order.form.postCode")}
          value={form.alternatePostCode}
        />
        <FieldRow label={t("order.form.state")} value={form.alternateState} />
        <FieldRow label={t("order.form.district")} value={form.alternateDistrict} />
        <FieldRow
          label={t("order.form.street")}
          value={form.alternateStreetAddress}
        />
        <FieldRow
          label={t("order.form.mobileNumber")}
          value={form.alternateMobileNumber}
        />
        <FieldRow
          label={t("order.form.company")}
          value={form.alternateCompanyName}
        />
        <FieldRow
          label={t("order.form.japaneseCompanyName")}
          value={form.alternateJapaneseCompanyName}
        />
        <FieldRow
          label={t("order.form.department")}
          value={form.alternateDepartment}
        />
        <Divider />

        <FieldRow
          label={t("order.form.deliveryTimeSlot")}
          value={form.deliveryTimeSlot}
        />
        <FieldRow label={t("order.form.remarks")} value={form.remarks} />
        <Divider />

        {form.order_items?.length > 0 && (
          <>
            <Heading size="sm">{t("order.form.orderItems")}</Heading>
            <Stack spacing={4}>
              {form.order_items.map((item, index) => (
                <Stack
                  key={item._id}
                  p={3}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg="gray.50"
                >
                  <FieldRow
                    label={t("order.form.item")}
                    value={`#${index + 1}`}
                  />
                  <FieldRow
                    label={t("order.form.itemType")}
                    value={item.itemType}
                  />

                  <FieldRow
                    label={t("order.form.item")}
                    value={item.package?.title || item.product?.title || "N/A"}
                  />
                  <FieldRow
                    label={t("order.form.quantity")}
                    value={item.quantity}
                  />

                  <FieldRow
                    label={t("order.form.price")}
                    value={`¥${Math.round(item.price)}`}
                  />
                  <FieldRow
                    label={t("order.form.taxRate")}
                    value={`${item.taxRate}%`}
                  />
                  <FieldRow
                    label={t("order.form.tax")}
                    value={`¥${Math.round(item.taxAmount)}`}
                  />
                  <FieldRow
                    label={t("order.form.shipmentCapacity")}
                    value={item.shipmentCapacity}
                  />
                  <FieldRow
                    label={t("order.form.category")}
                    value={item.category?.title}
                  />

                  {item.package && (
                    <FieldRow
                      label={t("order.form.package")}
                      value={item.package.title}
                    />
                  )}

                  {item.featuredImage && (
                    <Flex mt={2} align="center">
                      <Text mr={2}>{t("order.form.image")}:</Text>
                      <img
                        src={path() + item.featuredImage}
                        alt="Order item"
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                        }}
                      />
                    </Flex>
                  )}
                  {item.order_package_items?.length > 0 && (
                    <Card>
                      <Table variant="simple" maxWidth="50%">
                        <Thead bg="gray.100">
                          <Tr>
                            <Th width="100px">{t("common.list.sNo")}</Th>
                            <Th> {t("order.form.packageItems")}</Th>
                            <Th>{t("order.list.holdAction")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {item.order_package_items?.map((row, index) => {
                            return (
                              <Tr key={index} _hover={{ bg: "gray.50" }}>
                                <Td>
                                  <Text fontWeight="semibold">{index + 1}</Text>
                                </Td>
                                <Td pr={3}>
                                  <Text fontSize="sm" color="gray.500">
                                    {row.product.title}
                                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; (
                                    {row.group?.title})
                                  </Text>
                                </Td>
                                <Td pr={3}>
                                  <Checkbox
                                    isChecked={renderChecked(row._id, row)}
                                    onChange={(e) =>
                                      handleCheckboxChange(row._id)
                                    }
                                    disabled={
                                      form.status !==
                                      OrderStatusType.WAITING_FOR_ADMIN
                                    }
                                  ></Checkbox>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Card>
                  )}
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </FormControl>
  );
};

export default OrderDetailForm;
